import React, { useState, useEffect, useRef } from "react";
import logo from "../asset/pngImage/logo.png";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { useSelector } from "react-redux";
import useCart from "../hooks/useCart";
import { getSessionToken } from "../utils/auth";
import { useCartUI } from "../context/CartUIContext";
import useSettings from "../hooks/useSettings";

export default function Header(props) {
  const { fetchCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const cookieValue = getSessionToken();
  const settings = useSettings();
  const COOKIE_EXPIRY_DAYS = 3;

  const [searchTerm, setSearchTerm] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openCart } = useCartUI();
  const count = useSelector((state) => state.product.data);
  const searchRef = useRef(null);

  const phoneNumber = "9958344409";
  const message = encodeURIComponent("Hi, I am Interested in Bulk Purchase?");

  useEffect(() => {
    // Cookie-only checks here previously skipped fetchCart whenever the
    // session token lived only in localStorage — silent, no error, just a
    // stale cart badge sitewide. getSessionToken() checks both.
    const token = getSessionToken();
    const existingReferrer = Cookies.get("referrer");
    const currentReferrer = document.referrer;
    if (token) { fetchCart(); }
    if (currentReferrer && !existingReferrer) {
      const queryParams = new URLSearchParams(location.search);
      Cookies.set("referrer", JSON.stringify({
        referrer: currentReferrer,
        source: queryParams.get("utm_source") || queryParams.get("gad_source") || null,
        campaignId: queryParams.get("utm_campaign") || queryParams.get("gad_campaignid") || null,
      }), { expires: COOKIE_EXPIRY_DAYS });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only, to capture first-touch referrer/session
  }, [fetchCart]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus();
  }, [searchOpen]);

  useEffect(() => {
    setDrawerOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const goSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
      setSearchOpen(false);
    }
  };

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const navLinks = [
    { label: props.title       || "Home",            to: "/" },
    { label: props.Categories  || "Categories",      to: "/categories" },
    { label: props.Shop        || "Our Stores",      to: "/store" },
    { label: props.All         || "Wholesale Price", to: "/Khari-Baoli-Dry-Fruits-Rate-List-Today" },
  ];

  return (
    <>
      {/* ── Top announcement bar ── */}
      <div className="hdr-topbar">
        <div className="hdr-topbar-inner">
          {settings?.pin_free_delivery && (
            <>
              <span><i className="fas fa-truck me-1"></i>Free delivery above ₹{settings.pin_free_delivery} in Delhi NCR</span>
              <span className="hdr-topbar-sep">|</span>
            </>
          )}
          <span><i className="fas fa-phone-alt me-1"></i>+91 99583 44409</span>
          <span className="hdr-topbar-sep">|</span>
          <a href={`https://wa.me/${phoneNumber}?text=${message}`} target="_blank" rel="noreferrer" className="hdr-topbar-wa">
            <i className="fab fa-whatsapp me-1"></i>Bulk Enquiry
          </a>
        </div>
      </div>

      {/* ── Main navbar ── */}
      <header className={`hdr-nav${scrolled ? " hdr-nav--scrolled" : ""}`}>
        <div className="hdr-nav-inner">

          {/* ── LOGO — untouched original structure ── */}
          <Link to="/" className="logoheader hdr-logo-link">
            <img src="https://pub-637199cd2a854f238fa3851cb12f9eb1.r2.dev/static/zayadryfruits.png" alt="ZayaDryFruits" loading="lazy" style={{ height: "100%", width: "70%" }} />
          </Link>

          {/* ── Desktop nav links ── */}
          <nav className="hdr-links">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`hdr-link${isActive(link.to) ? " hdr-link--active" : ""}`}
              >
                {link.label}
                {isActive(link.to) && <span className="hdr-link-dot"></span>}
              </Link>
            ))}
          </nav>

          {/* ── Desktop search bar (inline) ── */}
          <div className="hdr-search-inline">
            <i className="fas fa-search hdr-search-inline-icon"></i>
            <input
              type="search"
              className="hdr-search-inline-input"
              placeholder="Search dry fruits, nuts…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && goSearch()}
            />
            <button className="hdr-search-inline-btn" onClick={goSearch}>
              Search
            </button>
          </div>

          {/* ── Right actions ── */}
          <div className="hdr-actions">
            {/* WhatsApp bulk */}
            <a
              href={`https://wa.me/${phoneNumber}?text=${message}`}
              target="_blank"
              rel="noreferrer"
              className="hdr-wa-btn"
              title="Bulk Enquiry"
            >
              <i className="fab fa-whatsapp"></i>
              <span>Bulk</span>
            </a>

            {/* Profile / Login */}
            {cookieValue == null ? (
              <Link to="/login" className="hdr-login-btn hdr-hide-mobile">
                <i className="fas fa-user"></i>
                <span>Login</span>
              </Link>
            ) : (
              <Link to="/profile" className="hdr-icon-btn hdr-hide-mobile" title="My Profile">
                <i className="fas fa-user-circle"></i>
              </Link>
            )}

            {/* Cart */}
            <button
              className="hdr-cart-btn hdr-hide-mobile"
              onClick={openCart}
              aria-label="Cart"
            >
              <i className="fas fa-shopping-bag"></i>
              {count?.length > 0 && <span className="hdr-cart-badge">{count.length}</span>}
            </button>

            {/* Mobile search toggle */}
            <button className="hdr-mob-search-btn hdr-icon-btn hdr-hide-mobile" onClick={() => setSearchOpen(!searchOpen)} aria-label="Search">
              <i className={`fas ${searchOpen ? "fa-times" : "fa-search"}`}></i>
            </button>

            {/* Hamburger */}
            <button className="hdr-hamburger" onClick={() => setDrawerOpen(true)} aria-label="Menu">
              <span></span><span></span><span></span>
            </button>
          </div>
        </div>

        {/* Mobile search dropdown */}
        <div className={`hdr-mob-search${searchOpen ? " hdr-mob-search--open" : ""}`}>
          <div className="hdr-mob-search-inner">
            <i className="fas fa-search"></i>
            <input
              ref={searchRef}
              type="search"
              placeholder="Search dry fruits, nuts, masala…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && goSearch()}
            />
            <button onClick={goSearch}>Go</button>
          </div>
        </div>
      </header>

      {/* ── Mobile Drawer overlay ── */}
      <div className={`hdr-overlay${drawerOpen ? " hdr-overlay--open" : ""}`} onClick={() => setDrawerOpen(false)} />

      {/* ── Mobile Drawer ── */}
      <div className={`hdr-drawer${drawerOpen ? " hdr-drawer--open" : ""}`}>
        <div className="hdr-drawer-head">
          {/* Logo in drawer — same original img */}
          <Link to="/" onClick={() => setDrawerOpen(false)}>
            <img src={logo} alt="ZayaDryFruits" style={{ height: 44, width: "auto" }} loading="lazy" />
          </Link>
          <button className="hdr-drawer-close" onClick={() => setDrawerOpen(false)}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <nav className="hdr-drawer-nav">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`hdr-drawer-link${isActive(link.to) ? " hdr-drawer-link--active" : ""}`}
              onClick={() => setDrawerOpen(false)}
            >
              <span>{link.label}</span>
              <i className="fas fa-chevron-right"></i>
            </Link>
          ))}
        </nav>

        <div className="hdr-drawer-footer">
          {cookieValue == null ? (
            <Link to="/login" className="hdr-drawer-cta hdr-drawer-cta--brown" onClick={() => setDrawerOpen(false)}>
              <i className="fas fa-sign-in-alt me-2"></i>Login / Register
            </Link>
          ) : (
            <Link to="/profile" className="hdr-drawer-cta hdr-drawer-cta--brown" onClick={() => setDrawerOpen(false)}>
              <i className="fas fa-user-circle me-2"></i>My Profile
            </Link>
          )}
          <a
            href={`https://wa.me/${phoneNumber}?text=${message}`}
            target="_blank"
            rel="noreferrer"
            className="hdr-drawer-cta hdr-drawer-cta--green"
          >
            <i className="fab fa-whatsapp me-2"></i>Bulk Enquiry on WhatsApp
          </a>
        </div>
      </div>
    </>
  );
}
